import { Component, ElementRef, effect, input, output, signal, viewChild } from '@angular/core';
import { normalizeCustomDateTimeForInput } from '../../utils/custom-datetime-display.util';

@Component({
  selector: 'app-datetime-picker',
  standalone: true,
  template: `
    <label class="datetime-shell">
      @if (label()) {
        <span>{{ label() }}</span>
      }
      <div class="datetime-control">
        <input
          type="text"
          class="datetime-text-input"
          inputmode="numeric"
          [required]="required()"
          [value]="displayValue()"
          placeholder="dd/mm/aaaa hh:mm"
          (input)="onTextInput($event)"
          (blur)="onTextBlur()"
          (keydown.enter)="onTextEnter($event)"
        />
        <button
          type="button"
          class="datetime-picker-btn"
          (click)="openNativePicker($event)"
          [attr.aria-label]="label() || 'Selecionar data e hora'"
        >
          <span aria-hidden="true"></span>
        </button>
        <input
          #nativeDatetimeInput
          type="datetime-local"
          class="datetime-native-input"
          [required]="required()"
          [value]="nativeValue()"
          (change)="onNativeDatetimeChange($event)"
        />
      </div>
    </label>
  `,
  styles: [
    `
      .datetime-shell {
        display: grid;
        gap: 4px;
        width: 100%;
        min-width: 0;
      }

      .datetime-shell span {
        font-size: 12px;
        color: var(--faint);
      }

      .datetime-control {
        position: relative;
      }

      .datetime-text-input {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        height: 36px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        padding: 0 10px;
        font-size: 13px;
        padding-right: 38px;
      }

      .datetime-picker-btn {
        position: absolute;
        top: 50%;
        right: 4px;
        transform: translateY(-50%);
        width: 28px;
        height: 28px;
        border: none;
        border-radius: 8px;
        background: transparent;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .datetime-picker-btn:hover {
        background: color-mix(in srgb, var(--hover) 65%, transparent);
      }

      .datetime-picker-btn span {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid var(--muted);
        border-radius: 3px;
        position: relative;
      }

      .datetime-picker-btn span::before {
        content: '';
        position: absolute;
        left: 2px;
        right: 2px;
        top: 4px;
        height: 2px;
        background: var(--muted);
      }

      .datetime-native-input {
        position: absolute;
        top: 0;
        right: 0;
        width: 36px;
        height: 36px;
        opacity: 0;
        cursor: pointer;
        pointer-events: none;
      }
    `,
  ],
})
export class DatetimePickerComponent {
  readonly label = input<string>('');
  readonly value = input<string | null>(null);
  readonly valueChange = output<string | null>();
  readonly required = input<boolean>(false);
  private readonly nativeDatetimeInput =
    viewChild<ElementRef<HTMLInputElement>>('nativeDatetimeInput');

  protected readonly displayValue = signal('');
  protected readonly nativeValue = signal<string>('');

  constructor() {
    effect(() => {
      const iso = this.normalizeIsoDatetime(this.value());
      this.nativeValue.set(iso ?? '');
      this.displayValue.set(iso ? this.formatIsoToPtBr(iso) : '');
    });
  }

  protected onTextInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.displayValue.set(value);
  }

  protected onTextBlur(): void {
    this.commitTextValue();
  }

  protected onTextEnter(event: Event): void {
    event.preventDefault();
    this.commitTextValue();
  }

  protected onNativeDatetimeChange(event: Event): void {
    const raw = (event.target as HTMLInputElement | null)?.value?.trim() ?? '';
    const iso = this.normalizeIsoDatetime(raw);
    if (!iso) {
      this.displayValue.set('');
      this.nativeValue.set('');
      this.valueChange.emit(null);
      return;
    }
    this.displayValue.set(this.formatIsoToPtBr(iso));
    this.nativeValue.set(iso);
    this.valueChange.emit(iso);
  }

  protected openNativePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const input = this.nativeDatetimeInput()?.nativeElement;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  }

  private commitTextValue(): void {
    const raw = this.displayValue().trim();
    if (!raw.length) {
      this.nativeValue.set('');
      this.valueChange.emit(null);
      return;
    }
    const parsed = this.parseDatetimeTextToIso(raw);
    if (!parsed) {
      const fallback = this.normalizeIsoDatetime(this.value());
      this.displayValue.set(fallback ? this.formatIsoToPtBr(fallback) : '');
      this.nativeValue.set(fallback ?? '');
      this.valueChange.emit(fallback);
      return;
    }
    this.displayValue.set(this.formatIsoToPtBr(parsed));
    this.nativeValue.set(parsed);
    this.valueChange.emit(parsed);
  }

  private parseDatetimeTextToIso(raw: string): string | null {
    const t = raw.trim();
    const iso = this.normalizeIsoDatetime(t);
    if (iso) return iso;
    // dd/mm/aaaa hh:mm ou dd/mm/aaaa hh:mm:ss
    const ptBr = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t);
    if (!ptBr) return null;
    const day = ptBr[1].padStart(2, '0');
    const month = ptBr[2].padStart(2, '0');
    const year = ptBr[3];
    const hour = ptBr[4].padStart(2, '0');
    const minute = ptBr[5];
    const second = ptBr[6] ?? '00';
    return this.normalizeIsoDatetime(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
  }

  /** Normaliza o valor para o datetime-local no fuso da sessao. */
  private normalizeIsoDatetime(raw: string | null | undefined): string | null {
    return normalizeCustomDateTimeForInput(raw);
  }

  /** Ex.: 2026-04-14T15:30:00 → 14/04/2026 15:30 */
  private formatIsoToPtBr(iso: string): string {
    const [datePart, timePart] = iso.split('T');
    const [y, m, d] = datePart.split('-');
    const [h, min] = timePart.split(':');
    return `${d}/${m}/${y} ${h}:${min}`;
  }
}
